import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import BasicDialog from "../../../components/base/BasicDialog";
import { SnackbarError } from "../../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../../components/base/SnackbarAlert/SnackbarSuccess";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import { useCreateOfferMutation } from "../../../services/offersService";
import { normalizePercentageForSubmission } from "../../../utils";
import { DialogTabs, DialogTabsEnum } from "./DialogTabs";
import { OfferEditDialog } from "./OfferEditDialog";
import { SubmitButton } from "./SubmitButton";

export const OfferCreateDialog = (props) => {
    const { t } = useTranslation();
    const { initialCustomerIds, initialProducts } = props;

    const [selectedTab, setSelectedTab] = useState(DialogTabsEnum.CUSTOMER_SELECTION);

    const form = useForm({
        resolver: zodResolver(createOfferFormDataSchema),
        defaultValues: {
            customerIds: initialCustomerIds || [],
            discount:
                initialProducts?.reduce((acc, product) => {
                    acc[product.productId] = normalizePercentageForSubmission(
                        product.discountPercent,
                    );
                    return acc;
                }, {}) || {},
        },
    });

    const { openDialog } = useDialogBetter();

    const handleOpenOfferEditDialog = (offer) => {
        openDialog({
            Component: OfferEditDialog,
            componentProps: {
                initialOffer: offer,
                initialTab: selectedTab,
            },
        });
    };

    const { mutate: createOffer, isPending } = useCreateOfferMutation({
        onSuccess: (offer) => {
            props.onClose();
            handleOpenOfferEditDialog(offer);
            toast.custom(
                (curToast) => (
                    <SnackbarSuccess message={t("offers.OfferCreateDialog.offerCreated")} />
                ),
                {
                    position: "bottom-right",
                },
            );
        },
        onError: (error) => {
            toast.custom(
                (curToast) => (
                    <SnackbarError message={t("offers.OfferCreateDialog.offerCreationFailed")} />
                ),
                {
                    position: "bottom-right",
                },
            );
        },
    });

    const onSubmit = (data) => {
        const requestBody = {
            customer_ids: data.customerIds,
            products: Object.entries(data.discount).map(([productId, discountPercent]) => ({
                product_id: productId,
                discount_percent: normalizePercentageForSubmission(discountPercent),
            })),
        };
        createOffer(requestBody);
    };

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            alwaysFullScreen={true}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("offers.OfferCreateDialog.title")}
            showDialogActions
        >
            <FormProvider {...form}>
                <form>
                    <DialogTabs
                        control={form.control}
                        fields={{
                            customerIds: "customerIds",
                            discount: "discount",
                        }}
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                    />
                    <SubmitButton
                        fields={{
                            customerIds: "customerIds",
                            discount: "discount",
                        }}
                        onSubmit={onSubmit}
                        isLoading={isPending}
                    />
                </form>
            </FormProvider>
        </BasicDialog>
    );
};

const createOfferFormDataSchema = z.object({
    customerIds: z.array(z.string().uuid()),
    discount: z.record(z.string().uuid(), z.number().min(0).max(100)),
});
